import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../../../components/Icon.tsx';

/**
 * Animated replica of the real WapSign sender flow, faithful to the app
 * (English/LTR): Upload → Editor (pick signers, place boxes) → Send for
 * signature → PENDING → signers sign one by one → COMPLETED. It steps through
 * these stages on a timer and holds on the completed state; the hosting
 * showcase remounts it each cycle, so it replays. Reduced-motion jumps straight
 * to the finished document.
 *
 * The chrome is WapSign's real Material 3 shell: a tinted chrome ground, a top
 * app bar, a Drive-style navigation drawer (tonal pill on the active
 * destination, tertiary-container "create" button) and a white content panel
 * with a large rounded top corner. Colours are set INLINE from WapSign's own M3
 * tokens so the replica never inherits Office's theme.
 */

/* WapSign's Material 3 light scheme (styles.css :root), as literal hex. */
const M3 = {
  chrome: '#F2F9F8',
  surface: '#FCFEFD',
  onSurface: '#181D1B',
  onSurfaceVariant: '#3F4946',
  outlineVariant: '#C3CDC8',
  primary: '#006A60',
  onPrimary: '#FFFFFF',
  primaryContainer: '#9EF2E4',
  onPrimaryContainer: '#00201C',
  secondaryContainer: '#CCE8E1',
  onSecondaryContainer: '#06201B',
  tertiaryContainer: '#CAE6FF',
  onTertiaryContainer: '#001E31',
  surfaceContainerLow: '#F4F8F6',
  surfaceContainer: '#EAF0EC',
  surfaceContainerHigh: '#E4EAE7',
  surfaceContainerHighest: '#DEE4E1',
  successContainer: '#B6F0CE',
  onSuccessContainer: '#062111',
  warningContainer: '#FFE08D',
  onWarningContainer: '#251A00',
  errorContainer: '#FFDAD6',
  error: '#BA1A1A',
} as const;

// WapSign's Latin UI type (--font-thaana): Material's Roboto stack.
const FONT: React.CSSProperties = {
  fontFamily: "'Roboto', 'Google Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
};
const TAB: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };

// The real navigation drawer's destinations (Sidebar.tsx, ORG_ADMIN nav).
const NAV = [
  { label: 'Documents', icon: 'description', active: true },
  { label: 'Inbox', icon: 'inbox', active: false },
  { label: 'Merge PDFs', icon: 'merge', active: false },
  { label: 'Compress PDF', icon: 'compress', active: false },
  { label: 'Profile', icon: 'person', active: false },
] as const;

const SIGNERS = [
  { name: 'Aishath Naz', role: 'President', color: '#0EA5A4', initials: 'AN' },
  { name: 'Ibrahim Rasheed', role: 'Secretary General', color: '#F59E0B', initials: 'IR' },
  { name: 'Mariyam Waheedha', role: 'Council Member', color: '#3B82F6', initials: 'MW' },
] as const;

// A distinct handwritten scribble per signer, drawn into the field when they
// sign (via the stroke-dash draw animation). pathLength=1 normalises the dash
// so the whole stroke reveals regardless of its real length.
const SIGNATURES = [
  // Aishath — loopy, rounded cursive
  'M5 22 C 3 9 17 7 17 19 C 17 28 9 28 12 17 C 16 5 31 10 34 22 C 37 31 46 28 53 19 C 61 10 71 20 83 14 C 95 9 103 18 113 11',
  // Ibrahim — sharp, angular zig-zag
  'M4 25 L 13 9 L 19 23 L 29 8 L 35 22 L 45 9 L 53 23 L 64 11 L 75 22 L 87 11 L 100 20 L 113 11',
  // Mariyam — compact scribble with a sweeping underline flourish
  'M6 17 C 9 7 16 8 15 17 C 14 25 22 25 24 16 C 27 7 34 10 36 19 C 38 27 45 25 51 18 C 58 11 66 18 74 14 M10 28 C 34 32 74 30 108 24',
];

// Milliseconds spent on each step before advancing to the next. index i = time
// held on step i. The final step (LAST) has no timer — it holds until remount.
const STEP_MS = [1300, 1450, 1400, 1750, 1000, 1350, 1150, 1150];
const LAST = 8;

export function WapSignScreen(): JSX.Element {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setStep(LAST);
      return;
    }
    // Loop: step through the flow, hold on the completed state, then replay.
    const ms = step >= LAST ? 2800 : STEP_MS[step] ?? 1300;
    const t = setTimeout(() => setStep((s) => (s >= LAST ? 0 : s + 1)), ms);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <Shell>
      {step <= 1 && <UploadStage step={step} />}
      {step >= 2 && step <= 4 && <EditorStage step={step} />}
      {step >= 5 && <DetailStage step={step} />}
    </Shell>
  );
}

/* ── The M3 app shell — top app bar + navigation drawer + content panel ── */
function Shell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      dir="ltr"
      className="app-screen h-full w-full overflow-hidden"
      style={{ ...FONT, background: M3.chrome, color: M3.onSurface }}
    >
      <div className="flex h-full flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <Drawer />
          <div
            className="min-w-0 flex-1 overflow-hidden rounded-t-2xl"
            style={{ background: M3.surface }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function Mark(): JSX.Element {
  // WapSign's app mark (public/icons/wapsign-mark.svg).
  return (
    <svg viewBox="0 0 512 512" aria-hidden className="size-6 shrink-0">
      <rect width="512" height="512" rx="128" fill="#14B8B7" />
      <path
        d="M149.3 138.7 H362.7 L345.6 218.7 C339.2 266.7 307.2 336 256 394.7 C204.8 336 172.8 266.7 166.4 218.7 Z"
        fill="#ffffff"
      />
      <circle cx="256" cy="215.5" r="22.4" fill="#14B8B7" />
      <line x1="256" y1="224" x2="256" y2="384" stroke="#14B8B7" strokeWidth={18.1} strokeLinecap="round" />
      <line x1="256" y1="384" x2="256" y2="426.7" stroke="#ffffff" strokeWidth={18.1} strokeLinecap="round" />
      <circle cx="256" cy="443.7" r="16" fill="#ffffff" />
    </svg>
  );
}

function TopBar(): JSX.Element {
  return (
    <div className="flex h-10 shrink-0 items-center gap-2 pl-2.5 pr-3">
      <Mark />
      <div className="min-w-0">
        <div className="text-[12px] font-medium leading-tight">WapSign</div>
        <div className="truncate text-[8.5px] leading-tight" style={{ color: M3.onSurfaceVariant }}>
          Ungoofaaru Council
        </div>
      </div>
      <div className="flex-1" />
      <span className="hidden sm:block" style={{ color: M3.onSurfaceVariant }}>
        <Icon name="light_mode" size={15} />
      </span>
      <span style={{ color: M3.onSurfaceVariant }}>
        <Icon name="logout" size={15} />
      </span>
      <span
        className="ml-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[9px] font-medium"
        style={{ background: M3.tertiaryContainer, color: M3.onTertiaryContainer }}
      >
        AN
      </span>
    </div>
  );
}

function Drawer(): JSX.Element {
  return (
    <div className="hidden w-[7.75rem] shrink-0 flex-col gap-0.5 px-1.5 pb-3 sm:flex">
      <span
        className="mb-2.5 inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-2xl py-2 pl-2 pr-3 text-[9.5px] font-medium"
        style={{
          background: M3.tertiaryContainer,
          color: M3.onTertiaryContainer,
          boxShadow: '0 1px 2px rgb(0 0 0 / 0.12)',
        }}
      >
        <Icon name="add" size={15} /> Send a document
      </span>

      {NAV.map((n) => (
        <span
          key={n.label}
          className="flex items-center gap-2 rounded-full px-2 py-1.5 text-[9.5px]"
          style={
            n.active
              ? { background: M3.secondaryContainer, color: M3.onSecondaryContainer, fontWeight: 500 }
              : { color: M3.onSurface }
          }
        >
          <span className="shrink-0" style={{ color: n.active ? 'inherit' : M3.onSurfaceVariant }}>
            <Icon name={n.icon} size={14} fill={n.active} />
          </span>
          <span className="truncate">{n.label}</span>
        </span>
      ))}
    </div>
  );
}

/* ── M3 chip — tonal, sentence case, 0.5rem radius, leading state dot ─── */
function Chip({
  bg,
  fg,
  children,
}: {
  bg: string;
  fg: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 text-[8.5px] font-medium"
      style={{ background: bg, color: fg, borderRadius: '0.5rem', letterSpacing: 0 }}
    >
      <span aria-hidden className="size-1 rounded-full" style={{ background: 'currentColor' }} />
      {children}
    </span>
  );
}

/* ── A single page of the document, as the PDF viewer renders it ──────── */
function DocPage({ children }: { children?: ReactNode }): JSX.Element {
  return (
    <div className="min-h-0 flex-1 p-2.5" style={{ background: M3.surfaceContainerLow }}>
      <div
        className="mx-auto h-full w-full max-w-[16rem] overflow-hidden rounded-lg px-4 py-3.5"
        style={{ background: '#fff', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)' }}
      >
        <p className="text-[7px] leading-tight" style={{ color: M3.onSurfaceVariant }}>
          Ungoofaaru Council · Ref 2026/14
        </p>
        <p className="mt-1.5 text-[12px] font-medium leading-tight">Council Resolution</p>
        <div className="mt-2 space-y-1">
          {[100, 94, 97, 62].map((w, i) => (
            <span
              key={i}
              className="block h-1 rounded-full"
              style={{ width: `${w}%`, background: M3.surfaceContainerHigh }}
            />
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Stage 1 — Upload ─────────────────────────────────────────────────── */
function UploadStage({ step }: { step: number }): JSX.Element {
  const picked = step >= 1;
  return (
    <div key="upload" className="ws-fade flex h-full flex-col px-4 pb-3 pt-4">
      <span className="text-[9px] font-medium" style={{ color: M3.onSurfaceVariant }}>
        Upload
      </span>
      <h1 className="mt-1 text-[1.35rem] font-normal leading-tight">Send a document</h1>
      <p className="mt-1.5 text-[9.5px] leading-snug" style={{ color: M3.onSurfaceVariant }}>
        Upload a PDF, then place signature and date boxes on the next step.
      </p>

      {picked ? (
        <div
          key="file"
          className="ws-pop mt-3.5 flex items-center gap-2.5 rounded-2xl p-2.5"
          style={{ background: M3.surfaceContainerLow }}
        >
          <span
            className="grid size-8 shrink-0 place-items-center rounded-full"
            style={{ background: M3.errorContainer, color: M3.error }}
          >
            <Icon name="description" size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[10px] font-medium">Council Resolution 2026-14.pdf</div>
            <div className="text-[8.5px]" style={{ ...TAB, color: M3.onSurfaceVariant }}>
              0.4 MB · 2 pages
            </div>
          </div>
          <span style={{ color: M3.primary }}>
            <Icon name="check" size={15} />
          </span>
        </div>
      ) : (
        <div
          className="mt-3.5 flex flex-col items-center justify-center gap-1.5 rounded-3xl py-4"
          style={{ background: M3.surfaceContainer }}
        >
          <span
            className="grid size-9 place-items-center rounded-full"
            style={{ background: M3.primaryContainer, color: M3.onPrimaryContainer }}
          >
            <Icon name="cloud_upload" size={19} />
          </span>
          <p className="text-[10px] font-medium">
            Drop a PDF here, or <span style={{ color: M3.primary }}>choose a file</span>
          </p>
          <p className="text-[8.5px]" style={{ color: M3.onSurfaceVariant }}>
            PDF · up to 25 MB
          </p>
        </div>
      )}

      <div className="mt-3">
        <label className="text-[9px] font-medium" style={{ color: M3.onSurfaceVariant }}>
          Title (optional)
        </label>
        <div
          className="mt-1 rounded-xl px-2.5 py-1.5 text-[10px]"
          style={{
            border: `1px solid ${M3.outlineVariant}`,
            color: picked ? M3.onSurface : M3.onSurfaceVariant,
          }}
        >
          {picked ? 'Council Resolution 2026/14' : 'e.g. NDA — Acme Co.'}
        </div>
      </div>

      <div className="mt-3.5 flex justify-end">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[10px] font-medium transition-all"
          style={
            picked
              ? { background: M3.primary, color: M3.onPrimary }
              : { background: M3.surfaceContainerHigh, color: M3.onSurfaceVariant }
          }
        >
          Continue <Icon name="arrow_forward" size={13} />
        </span>
      </div>
    </div>
  );
}

/* ── Stage 2 — Editor (place signers + boxes, send) ───────────────────── */
function EditorStage({ step }: { step: number }): JSX.Element {
  const placed = step >= 3; // signers chosen + boxes dropped
  const sending = step >= 4;

  return (
    <div key="editor" className="ws-fade flex h-full flex-col">
      {/* editor top row — back, title + draft meta, send */}
      <div className="flex shrink-0 items-center gap-2 px-2.5 py-2">
        <span className="grid size-6 shrink-0 place-items-center rounded-full" style={{ color: M3.onSurfaceVariant }}>
          <Icon name="arrow_back" size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-medium leading-tight">Council Resolution 2026/14</div>
          <div className="truncate text-[8.5px] leading-tight" style={{ ...TAB, color: M3.onSurfaceVariant }}>
            Draft · {placed ? '3 placeholders · 3 signers' : '0 placeholders · 0 signers'}
          </div>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-medium"
          style={{ background: M3.primary, color: M3.onPrimary, opacity: sending ? 0.85 : 1 }}
        >
          <Icon name="send" size={12} />
          {sending ? 'Sending…' : 'Send for signature'}
        </span>
      </div>

      {/* placement toolbar */}
      <div
        className="mx-2.5 flex shrink-0 items-center gap-1 rounded-2xl px-2 py-1"
        style={{ background: M3.surfaceContainerLow }}
      >
        <span className="mr-0.5 shrink-0 text-[8.5px]" style={{ color: M3.onSurfaceVariant }}>
          Place a box
        </span>
        <span className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[9px] font-medium"
          style={{ background: M3.secondaryContainer, color: M3.onSecondaryContainer }}>
          <Icon name="stylus_note" size={12} /> Signature
        </span>
        <span className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[9px] font-medium">
          <Icon name="calendar_month" size={12} /> Sign date
        </span>
      </div>

      <div className="mt-1.5 grid min-h-0 flex-1 grid-cols-[1fr_7.5rem] gap-1.5 px-2.5 pb-2.5">
        {/* PDF preview with placed boxes */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl">
          <DocPage>
            {placed && (
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                {SIGNERS.map((s) => (
                  <div
                    key={s.name}
                    className="ws-pop relative rounded border-2 px-1 pb-0.5 pt-2"
                    style={{ borderColor: s.color, background: `${s.color}18` }}
                  >
                    <span
                      className="absolute -top-1.5 left-0.5 rounded px-1 text-[6px] font-medium"
                      style={{ background: s.color, color: '#fff' }}
                    >
                      {s.name.split(' ')[0]}
                    </span>
                    <span style={{ color: s.color }}>
                      <Icon name="stylus_note" size={11} />
                    </span>
                    <div className="mt-1 h-px" style={{ background: `${s.color}80` }} />
                  </div>
                ))}
              </div>
            )}
          </DocPage>
        </div>

        {/* signers aside */}
        <div className="min-h-0 overflow-hidden rounded-2xl px-2 py-2" style={{ background: M3.surfaceContainerLow }}>
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-medium" style={{ color: M3.onSurfaceVariant }}>
              Signers
            </span>
            <span className="text-[9px]" style={{ ...TAB, color: M3.onSurfaceVariant }}>
              {placed ? '3 / 3' : '0 / 3'}
            </span>
          </div>
          <div className="mt-1.5 space-y-1">
            {SIGNERS.map((s) => (
              <div
                key={s.name}
                className="flex items-center gap-1.5 rounded-xl px-1.5 py-1 transition-all"
                style={
                  placed
                    ? { background: M3.secondaryContainer, color: M3.onSecondaryContainer }
                    : { color: M3.onSurface }
                }
              >
                <span
                  className="grid size-4 shrink-0 place-items-center rounded-full text-[6px] font-semibold text-white"
                  style={{ background: s.color, filter: placed ? 'none' : 'grayscale(1)' }}
                >
                  {s.initials}
                </span>
                <span className="truncate text-[8.5px]">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Stage 3 — Detail (pending → signing → completed) ─────────────────── */
function DetailStage({ step }: { step: number }): JSX.Element {
  // step 5: sent, all pending. 6: 1 signed. 7: 2 signed. 8: all signed → completed.
  const signedCount = step <= 5 ? 0 : step === 6 ? 1 : step === 7 ? 2 : 3;
  const completed = signedCount === 3;

  return (
    <div key="detail" className="ws-fade flex h-full flex-col">
      {/* document header */}
      <div className="flex shrink-0 items-start justify-between gap-2 px-3 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {completed ? (
              <Chip bg={M3.successContainer} fg={M3.onSuccessContainer}>Completed</Chip>
            ) : (
              <Chip bg={M3.warningContainer} fg={M3.onWarningContainer}>Pending</Chip>
            )}
            <span className="text-[8.5px]" style={{ ...TAB, color: M3.onSurfaceVariant }}>
              Sent 09 Jul 2026
            </span>
          </div>
          <p className="mt-1 truncate text-[13px] font-normal leading-tight">Council Resolution 2026/14</p>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-medium"
          style={{ background: M3.secondaryContainer, color: M3.onSecondaryContainer }}
        >
          <Icon name="download" size={12} /> {completed ? 'Signed' : 'Original'}
        </span>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1fr_7.5rem] gap-1.5 px-2.5 pb-2.5">
        {/* document with signatures filling in */}
        <div className="relative flex min-h-0 flex-col overflow-hidden rounded-xl">
          <DocPage>
            <div className="mt-3 grid grid-cols-3 gap-1.5">
              {SIGNERS.map((s, i) => {
                const signed = i < signedCount;
                return (
                  <div
                    key={s.name}
                    className="relative rounded px-1 pb-0.5 pt-2"
                    style={{
                      border: signed ? `1px solid ${M3.outlineVariant}` : `2px solid ${s.color}`,
                      background: signed ? 'transparent' : `${s.color}12`,
                    }}
                  >
                    {signed ? (
                      <svg
                        key="sig"
                        viewBox="0 0 116 32"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="ws-sign h-5 w-full"
                      >
                        <path
                          d={SIGNATURES[i]}
                          pathLength={1}
                          stroke={M3.onSurface}
                          strokeWidth={2.4}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <span style={{ color: s.color }}>
                        <Icon name="stylus_note" size={11} />
                      </span>
                    )}
                    <div className="mt-1 h-px" style={{ background: M3.outlineVariant }} />
                    {signed && (
                      <div className="mt-0.5 flex items-center gap-0.5" style={{ color: M3.primary }}>
                        <Icon name="check" size={8} />
                        <span className="text-[6px]" style={{ ...TAB, color: M3.onSurfaceVariant }}>
                          12 Jul
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </DocPage>

          {completed && (
            <div
              key="stamp"
              aria-hidden
              className="ws-pop absolute bottom-3 right-4 flex size-12 rotate-[-12deg] flex-col items-center justify-center rounded-full"
              style={{ border: `2px solid ${M3.error}B3`, color: M3.error, background: '#ffffffb3' }}
            >
              <Icon name="approval" size={12} />
              <span className="mt-0.5 text-[6px] font-medium">APPROVED</span>
            </div>
          )}
        </div>

        {/* signers status list */}
        <div className="min-h-0 overflow-hidden rounded-2xl px-2 py-2" style={{ background: M3.surfaceContainerLow }}>
          <span className="text-[9px] font-medium" style={{ ...TAB, color: M3.onSurfaceVariant }}>
            Signers · {signedCount} of 3
          </span>
          <ul className="mt-1.5 space-y-1.5">
            {SIGNERS.map((s, i) => {
              const signed = i < signedCount;
              return (
                <li key={s.name} className="flex items-start gap-1.5">
                  <span
                    className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full text-[6px] font-semibold"
                    style={{ background: M3.primaryContainer, color: M3.onPrimaryContainer }}
                  >
                    {s.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[8.5px] font-medium leading-tight">{s.name}</div>
                    <div className="truncate text-[7.5px] leading-tight" style={{ color: M3.onSurfaceVariant }}>
                      1 box
                    </div>
                    <div className="mt-1">
                      {signed ? (
                        <Chip bg={M3.successContainer} fg={M3.onSuccessContainer}>Signed</Chip>
                      ) : (
                        <Chip bg={M3.warningContainer} fg={M3.onWarningContainer}>Pending</Chip>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {completed && (
            <div
              key="done"
              className="ws-rise mt-2 text-[7.5px]"
              style={{ ...TAB, color: M3.onSurfaceVariant }}
            >
              Completed 12 Jul 2026
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
